import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { LinkifiedText } from '../ui/LinkifiedText';
import { medallionLabel, SignalMedallion } from './PublicSignalMedallion';
import type { FetchedSignal } from '../../api/dataApi';
import type { Template } from '../../types';

// Renders one bunchMessages() group (src/lib/bunchMessages.ts) the same
// way the private app's MessagesScreen.tsx BunchStack does: a single
// group collapses into ONE compact card - the newest message's text,
// an "N messages" count, and 1-2 faded "peek" card edges stacked behind
// it to read as a pile rather than a flat list. Tapping expands it into
// individual rows, same as the app. A group of exactly one message skips
// all of that and renders as a plain row, same as before bunching
// existed. Shared by PublicChannelPageScreen.tsx and
// PublicReportScreen.tsx so both read as "the app," not a flat dump of
// every message.
export function BunchedMessageGroup({
  group,
  templates,
  dividerClassName,
}: {
  group: FetchedSignal[];
  templates: Template[];
  dividerClassName: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const head = group[0];

  if (group.length === 1) {
    return (
      <div className="flex items-start gap-3 py-4">
        <SignalMedallion message={head} templates={templates} />
        <div className="flex flex-col gap-1 min-w-0 flex-1">
          <span className="text-xs font-semibold text-black/50 dark:text-white/50">{head.sender || 'Unknown'}</span>
          <LinkifiedText text={medallionLabel(head)} className="text-[15px] whitespace-pre-wrap" />
          <span className="text-[11px] text-black/40 dark:text-white/40">
            {head.date ? new Date(head.date).toLocaleString() : ''}
          </span>
        </div>
      </div>
    );
  }

  if (expanded) {
    return (
      <div className="py-2">
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="flex items-center gap-1.5 text-xs font-semibold text-black/40 dark:text-white/40 hover:text-black/60 dark:hover:text-white/60 mb-1"
        >
          <ChevronUp size={14} />
          Collapse {group.length} messages
        </button>
        {group.map((message, i) => (
          <div key={message.id} className={`flex items-start gap-3 py-3 ${i > 0 ? `border-t ${dividerClassName}` : ''}`}>
            <SignalMedallion message={message} templates={templates} />
            <div className="flex flex-col gap-1 min-w-0 flex-1">
              <span className="text-xs font-semibold text-black/50 dark:text-white/50">{message.sender || 'Unknown'}</span>
              <LinkifiedText text={medallionLabel(message)} className="text-[15px] whitespace-pre-wrap" />
              <span className="text-[11px] text-black/40 dark:text-white/40">
                {message.date ? new Date(message.date).toLocaleString() : ''}
              </span>
            </div>
          </div>
        ))}
      </div>
    );
  }

  const peekCount = Math.min(group.length - 1, 2);
  return (
    <div className="py-4">
      <button type="button" onClick={() => setExpanded(true)} className="relative block w-full text-left group/stack">
        {Array.from({ length: peekCount }).map((_, i) => (
          <div
            key={i}
            aria-hidden="true"
            className="absolute inset-x-0 rounded-xl border border-black/10 dark:border-white/10 bg-white dark:bg-white/5"
            style={{
              top: (i + 1) * 5,
              left: (i + 1) * 5,
              right: -(i + 1) * 5,
              height: 64,
              opacity: 1 - (i + 1) * 0.3,
              zIndex: peekCount - i,
            }}
          />
        ))}
        <div
          className="relative flex items-start gap-3 rounded-xl border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 p-3 group-hover/stack:border-black/20 dark:group-hover/stack:border-white/20 transition-colors"
          style={{ zIndex: peekCount + 1 }}
        >
          <SignalMedallion message={head} templates={templates} />
          <div className="flex flex-col gap-1 min-w-0 flex-1">
            <span className="text-xs font-semibold text-black/50 dark:text-white/50 flex items-center gap-1.5">
              {head.sender || 'Unknown'}
              <span className="font-normal text-black/40 dark:text-white/40">· {group.length} messages</span>
            </span>
            <LinkifiedText text={medallionLabel(head)} className="text-[15px] whitespace-pre-wrap truncate" />
            <span className="text-[11px] text-black/40 dark:text-white/40">
              {head.date ? new Date(head.date).toLocaleString() : ''}
            </span>
          </div>
          <ChevronDown size={16} className="shrink-0 text-black/30 dark:text-white/30 mt-1" />
        </div>
      </button>
    </div>
  );
}
